<template>
  <div class="py-8">
    <v-app-bar flat color="transparent">
      <app-bar-nav-icon />
      <v-toolbar-title class="breadcrumbs d-flex align-center">
        <v-icon class="text-light-blue">{{ icons.arrowLeft }}</v-icon>
        <router-link class="text-decoration--none" :to="{ name: 'videos' }">
          Video</router-link
        >
      </v-toolbar-title>
    </v-app-bar>

    <div class="ma-4">
      <div class="display-1 font-weight-bold my-5 text-capitalize">
        Add Video
      </div>

      <div class="col-xl-4 col-lg-6 col-md-12 mt-6 pa-0">
        <v-form @submit.prevent="submit" ref="form">
          <v-divider class="pb-5"></v-divider>

          <label class="text-field-label">Name</label>
          <v-text-field
            flat
            solo
            required
            counter
            class="mt-2"
            :rules="rules.name"
            maxlength="255"
            v-model="form.name"
            :error-messages="form.$getError('name')"
          ></v-text-field>

          <v-alert v-if="form.$getError('file_path')" type="error" class="mt-5">
            {{ form.$getError('file_path') }}
          </v-alert>

          <div>
            <vue-dropzone
              ref="myVueDropzone"
              id="dropzone"
              :options="dropzoneOptions"
              @vdropzone-success="uploadSuccess"
              @vdropzone-error="uploadError"
              @vdropzone-sending="uploadSending"
              @vdropzone-queue-complete="queueComplete"
              @vdropzone-total-upload-progress="updateTotalProgress"
            ></vue-dropzone>

            <v-progress-linear
              v-if="loading"
              :active="loading"
              :value="uploadProgress"
              :indeterminate="isFinalizing || (loading && uploadProgress === 0)"
              color="deep-purple accent-4"
              rounded
              height="6"
            >
              <template v-if="isFinalizing" v-slot:default>
                <strong class="white--text">Finalizing...</strong>
              </template>
            </v-progress-linear>
          </div>

          <span class="red--text mt-4" v-if="loading">
            Do not leave this page while video upload is in progress
          </span>

          <v-divider class="my-5"></v-divider>

          <div class="mt-4">
            <v-btn
              type="submit"
              color="primary"
              :loading="form.$busy"
              :disabled="loading"
            >
              Submit
            </v-btn>
          </div>
        </v-form>
      </div>
    </div>

    <v-snackbar v-model="snackbar.show" right :color="snackbar.color">
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text @click="snackbar.show = false" v-bind="attrs">OK</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import { mapActions } from 'vuex'
import { mdiChevronLeft } from '@mdi/js'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import Cookies from 'js-cookie'
import Form from '@/utils/form'
import vue2Dropzone from 'vue2-dropzone'
import axios from 'axios'
import { each } from 'lodash'

export default {
  components: {
    vueDropzone: vue2Dropzone,
    AppBarNavIcon,
  },

  data: function () {
    return {
      CHUNK_THRESHOLD: 25 * 1024 * 1024,

      dropzoneOptions: {
        url: this.getUploadUrl(),
        headers: {
          Authorization:
            'Bearer ' + Cookies.getJSON('AUTH_TOKEN')['access_token'],
        },
        parallelUploads: 1,
        maxFilesize: 750, // 750MB
        maxFiles: 100,
        retryChunks: true,
        addRemoveLinks: true,
        autoProcessQueue: false,
        timeout: 600000,
        acceptedFiles: 'video/*',
      },

      icons: {
        arrowLeft: mdiChevronLeft,
      },

      rules: {
        name: [(v) => !!v || 'Name is required'],
      },

      form: new Form({
        name: '',
        file_path: null,
      }),

      snackbar: {
        show: false,
        message: null,
        color: '',
      },

      loading: false,
      uploadProgress: 0,
      currentFile: null,
      uploadId: null,
      isChunkedUpload: false,
      isFinalizing: false,
      uploadedChunks: new Set(),
      totalChunks: 0,
      chunkRetries: new Map(),
      maxRetries: 3,
    }
  },

  methods: {
    ...mapActions({
      storeVideo: 'video/storeVideo',
    }),

    getUploadUrl() {
      return process.env.VUE_APP_API_URL + '/admin/videos/upload'
    },

    getChunkedUploadUrl() {
      return (
        process.env.VUE_APP_API_URL +
        '/admin/videos/chunked-upload/upload-chunk'
      )
    },

    async submit() {
      if (!this.$refs.form.validate()) return

      const filesInQueue = this.$refs.myVueDropzone.getQueuedFiles()
      if (filesInQueue.length === 0) {
        this.showSnackbar('Please select a video file', 'error')
        return
      }

      this.loading = true
      this.uploadProgress = 0
      const originalFile = filesInQueue[0]
      this.currentFile = originalFile // Keep a reference

      // Reset chunk tracking
      this.uploadedChunks.clear()
      this.chunkRetries.clear()
      this.totalChunks = 0

      // Decide upload method based on file size
      this.isChunkedUpload = originalFile.size > this.CHUNK_THRESHOLD

      if (this.isChunkedUpload) {
        await this.initializeChunkedUpload()
      } else {
        this.setupRegularUpload()
      }

      // CRITICAL FIX: To defeat state-staleness, we remove the original file
      // and re-add it. This forces Dropzone to create a new file object with
      // the LATEST options (including chunking).
      this.$refs.myVueDropzone.removeFile(originalFile)
      this.$refs.myVueDropzone.addFile(originalFile)
      this.$refs.myVueDropzone.processQueue()
    },

    async initializeChunkedUpload() {
      try {
        this.totalChunks = Math.ceil(
          this.currentFile.size / this.CHUNK_THRESHOLD
        )

        const response = await axios.post(
          process.env.VUE_APP_API_URL + '/admin/videos/chunked-upload/initiate',
          {
            filename: this.currentFile.name,
            total_size: this.currentFile.size,
            total_chunks: this.totalChunks,
            chunk_size: this.CHUNK_THRESHOLD,
          },
          {
            headers: {
              Authorization:
                'Bearer ' + Cookies.getJSON('AUTH_TOKEN')['access_token'],
            },
          }
        )

        this.uploadId = response.data.upload_id

        // Update dropzone options for chunked upload
        this.$refs.myVueDropzone.dropzone.options.url =
          this.getChunkedUploadUrl()
        this.$refs.myVueDropzone.dropzone.options.method = 'POST'
        this.$refs.myVueDropzone.dropzone.options.chunking = true
        this.$refs.myVueDropzone.dropzone.options.paramName = 'chunk'
        this.$refs.myVueDropzone.dropzone.options.chunkSize =
          this.CHUNK_THRESHOLD
        this.$refs.myVueDropzone.dropzone.options.parallelChunkUploads = false
        this.$refs.myVueDropzone.dropzone.options.retryChunks = true
        this.$refs.myVueDropzone.dropzone.options.retryChunksLimit =
          this.maxRetries

        this.$refs.myVueDropzone.dropzone.options.params = (
          files,
          xhr,
          chunk
        ) => {
          if (chunk) {
            const chunkNumber = chunk.index + 1

            xhr.upload.addEventListener('progress', (e) => {
              if (e.lengthComputable) {
                const chunkProgress = e.loaded / e.total
                const overallProgress = Math.min(
                  100,
                  ((this.uploadedChunks.size + chunkProgress) /
                    this.totalChunks) *
                    100
                )
                this.updateTotalProgress(overallProgress)
              }
            })

            xhr.upload.addEventListener('load', () => {
              this.uploadedChunks.add(chunkNumber)
              this.chunkRetries.delete(chunkNumber)
              const overallProgress = Math.min(
                100,
                (this.uploadedChunks.size / this.totalChunks) * 100
              )
              this.updateTotalProgress(overallProgress)
            })

            xhr.upload.addEventListener('error', () => {
              const retryCount = this.chunkRetries.get(chunkNumber) || 0
              if (retryCount < this.maxRetries) {
                this.chunkRetries.set(chunkNumber, retryCount + 1)
                // console.info(
                //   `Retrying chunk ${chunkNumber}, attempt ${retryCount + 1}`
                // )
              } else {
                // console.error(
                //   `Chunk ${chunkNumber} failed after ${this.maxRetries} retries`
                // )
              }
            })

            return { upload_id: this.uploadId, chunk_number: chunkNumber }
          }
          return {}
        }
      } catch (error) {
        this.loading = false
        this.showSnackbar('Failed to initialize upload', 'error')
      }
    },

    setupRegularUpload() {
      // Reset dropzone options for regular upload
      this.$refs.myVueDropzone.dropzone.options.url = this.getUploadUrl()
      this.$refs.myVueDropzone.dropzone.options.method = 'POST'
      this.$refs.myVueDropzone.dropzone.options.chunking = false
      this.$refs.myVueDropzone.dropzone.options.paramName = 'file' // reset to default
      delete this.$refs.myVueDropzone.dropzone.options.chunkSize
      delete this.$refs.myVueDropzone.dropzone.options.parallelChunkUploads
      this.$refs.myVueDropzone.dropzone.options.params = null
    },

    uploadSending(file, xhr, formData) {
      if (!this.isChunkedUpload) {
        formData.append('name', this.form.name)
      }
    },

    async uploadSuccess(_file, response) {
      if (this.isChunkedUpload) {
        // For chunked uploads, verify all chunks are uploaded before finalizing
        if (this.uploadedChunks.size === this.totalChunks) {
          this.uploadProgress = 100
          this.isFinalizing = true
          await this.finalizeChunkedUpload()
        } else {
          // console.info(
          //   `Upload completed but only ${this.uploadedChunks.size}/${this.totalChunks} chunks received`
          // )
          // Wait a bit for potential delayed chunk confirmations
          setTimeout(async () => {
            if (this.uploadedChunks.size === this.totalChunks) {
              this.uploadProgress = 100
              this.isFinalizing = true
              await this.finalizeChunkedUpload()
            } else {
              await this.retryMissingChunks()
            }
          }, 2000)
        }
      } else {
        this.uploadProgress = 100
        // Store the file path from the upload response
        if (response && response.path) {
          this.form.file_path = response.path
        }
        this.loading = false
        this.completeUpload()
        this.submitForm()
      }
    },

    async retryMissingChunks() {
      try {
        // Get the status of chunks from server
        const statusResponse = await axios.get(
          process.env.VUE_APP_API_URL +
            `/admin/videos/chunked-upload/status/${this.uploadId}`,
          {
            headers: {
              Authorization:
                'Bearer ' + Cookies.getJSON('AUTH_TOKEN')['access_token'],
            },
          }
        )

        const serverChunks = new Set(statusResponse.data.uploaded_chunks || [])
        const missingChunks = []

        for (let i = 1; i <= this.totalChunks; i++) {
          if (!serverChunks.has(i)) {
            missingChunks.push(i)
          }
        }

        if (missingChunks.length === 0) {
          // All chunks are actually uploaded, proceed to finalize
          this.uploadedChunks = new Set(
            [...Array(this.totalChunks).keys()].map((i) => i + 1)
          )
          this.uploadProgress = 100
          this.isFinalizing = true
          await this.finalizeChunkedUpload()
        } else {
          this.showSnackbar(
            `Retrying ${missingChunks.length} missing chunks...`,
            'warning'
          )
          // Force re-upload of missing chunks by re-processing the file
          this.$refs.myVueDropzone.processQueue()
        }
      } catch (error) {
        // console.error('Failed to check chunk status:', error)
        this.showSnackbar('Failed to verify upload status', 'error')
        this.loading = false
        this.isFinalizing = false
      }
    },

    async finalizeChunkedUpload() {
      try {
        // Double-check all chunks are uploaded before finalizing
        if (this.uploadedChunks.size !== this.totalChunks) {
          // console.info(
          //   `Cannot finalize: ${this.uploadedChunks.size}/${this.totalChunks} chunks uploaded`
          // )
          await this.retryMissingChunks()
          return
        }

        const response = await axios.post(
          process.env.VUE_APP_API_URL + '/admin/videos/chunked-upload/finalize',
          {
            upload_id: this.uploadId,
            name: this.form.name,
          },
          {
            headers: {
              Authorization:
                'Bearer ' + Cookies.getJSON('AUTH_TOKEN')['access_token'],
            },
          }
        )

        if (response && response.data) {
          if (response.data.success) {
            // Store the file path from the finalize response
            if (response.data.path) {
              this.form.file_path = response.data.path
            }
            this.loading = false
            this.isFinalizing = false
            this.completeUpload()
            this.submitForm()
          } else {
            // Server reports not all chunks uploaded, retry missing ones
            const uploaded = response.data.uploaded || 0
            const total = response.data.total || this.totalChunks
            this.showSnackbar(
              `Only ${uploaded}/${total} chunks uploaded. Retrying...`,
              'warning'
            )
            this.isFinalizing = false
            await this.retryMissingChunks()
          }
        }
      } catch (error) {
        // console.error('Finalize error:', error)
        let errorMessage = 'Failed to finalize upload'

        if (error.response && error.response.data) {
          if (error.response.data.message) {
            errorMessage = error.response.data.message
          }

          // Handle specific "Not all chunks uploaded" error
          if (error.response.data.uploaded && error.response.data.total) {
            const uploaded = error.response.data.uploaded
            const total = error.response.data.total
            this.showSnackbar(
              `Only ${uploaded}/${total} chunks uploaded. Retrying...`,
              'warning'
            )
            this.isFinalizing = false
            await this.retryMissingChunks()
            return
          }
        }

        this.showSnackbar(errorMessage, 'error')
        this.loading = false
        this.isFinalizing = false
      }
    },

    completeUpload() {
      this.showSnackbar('Video successfully uploaded!', 'success')
    },

    uploadError(_file, message, xhr) {
      // console.error('Upload error:', { message, xhr })

      // Don't immediately fail if this is a chunk upload error - let retry logic handle it
      if (this.isChunkedUpload && xhr && xhr.status >= 500) {
        // Server error - let the retry mechanism handle it
        // console.info('Server error on chunk upload, will retry...')
        return
      }

      this.loading = false
      this.isFinalizing = false
      this.uploadProgress = 0

      let errorMessage = 'Upload failed'

      if (xhr && xhr.status === 422) {
        // Validation errors
        try {
          const errors = JSON.parse(message)
          if (errors.errors) {
            this.form.$setErrors(errors.errors)
            return
          }
        } catch (e) {
          // Parse error, use default message
        }
      }

      if (typeof message === 'string') {
        errorMessage = message
      } else if (message && message.message) {
        errorMessage = message.message
      }

      this.showSnackbar(errorMessage, 'error')

      // Cancel chunked upload if it was in progress and this is a critical error
      if (this.isChunkedUpload && this.uploadId) {
        this.cancelChunkedUpload()
      }
    },

    async cancelChunkedUpload() {
      try {
        await axios.post(
          process.env.VUE_APP_API_URL +
            `/admin/videos/chunked-upload/cancel/${this.uploadId}`,
          {},
          {
            headers: {
              Authorization:
                'Bearer ' + Cookies.getJSON('AUTH_TOKEN')['access_token'],
            },
          }
        )
      } catch (error) {
        this.showSnackbar('Failed to cancel chunked upload', 'error')
      }

      this.uploadId = null
    },

    queueComplete() {
      // Called when all files in queue are processed
      if (!this.loading) {
        this.uploadProgress = 0
      }
    },

    submitForm() {
      this.form.$busy = true
      this.form.$clearErrors()

      const formData = new FormData()

      each(this.form.$data(), (value, key) => {
        formData.append(key, value)
      })

      this.storeVideo(formData)
        .then(() => {
          this.showSnackbar('Videos successfully created!', 'success')
          setTimeout(() => {
            this.$router.push({
              name: 'videos',
            })
          }, 1000)
        })
        .catch((err) => {
          if (err.response.status === 422) {
            this.form.$setErrors(err.response.data.errors)
          }
          this.form.$busy = false
        })
    },

    showSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.show = true
      this.snackbar.color = color
    },

    updateTotalProgress(totalProgress) {
      this.uploadProgress = totalProgress
    },
  },

  beforeDestroy() {
    // Cancel any ongoing chunked upload when component is destroyed
    if (this.isChunkedUpload && this.uploadId && this.loading) {
      this.cancelChunkedUpload()
    }
  },
}
</script>
